import type { Store } from './storeService';
import { getApiBaseUrl, getHeaders } from './apiClient';

const API_URL = typeof window === 'undefined'
  ? (process.env.VITE_API_URL || import.meta.env.VITE_API_URL || 'http://localhost:8080/api')
  : import.meta.env.VITE_API_URL;

// ─── Types ────────────────────────────────────────────────────────────────────

export type StoreSearchHit = {
  id: string;
  slug: string;
  name: string;
  nameEn?: string;
  description?: string;
  tags: string[];
  categoryIds: string[];
  categorySlugs: string[];
  categoryNamesAr: string[];
  location?: string;
  governorates: string[];
  paymentOptions: string[];
  rating: number;
  totalReviews: number;
  logoUrl?: string;
  hasInstagram: boolean;
  hasFacebook: boolean;
  hasWebsite: boolean;
  _rankingScore?: number; // present only when showRankingScore was requested
};

export type CategorySearchHit = {
  id: string;
  titleAr: string;
  titleEn: string;
  slug: string;
};

export type SearchStoresParams = {
  q: string;
  limit?: number;
  offset?: number;
  categoryIds?: string[];
  hasFacebook?: boolean;
  hasInstagram?: boolean;
  hasWebsite?: boolean;
  minRating?: number;
  sort?: string[]; // Meilisearch sort expressions, e.g. 'yetfarragRating:desc' — backend whitelist-validates
  showRankingScore?: boolean; // ask Meili for _rankingScore per hit (also feeds topScore telemetry)
  lexicalOnly?: boolean; // sends semanticRatio=0 — skips the embedding round-trip (autocomplete)
};

export type SearchStoresResponse = {
  ok: true;
  data: {
    hits: StoreSearchHit[];
    totalHits: number;
    facetDistribution?: Record<string, Record<string, number>>;
    // Hits on this page that came from the semantic (vector) side of the hybrid
    // merge. Equal to hits.length ⇒ the query matched nothing lexically.
    semanticHitCount?: number;
  };
};

export type SearchCategoriesResponse = {
  ok: true;
  data: {
    hits: CategorySearchHit[];
    totalHits: number;
  };
};

export type SelectedFacets = {
  categoryIds: string[];
  hasFacebook: boolean;
  hasInstagram: boolean;
  hasWebsite: boolean;
  minRating: number; // 0 = no rating filter
};

// ─── API Functions ─────────────────────────────────────────────────────────────

export const searchHybrid = async (params: SearchStoresParams): Promise<SearchStoresResponse> => {
  const queryParams = new URLSearchParams();
  queryParams.append('q', params.q);
  if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params.offset !== undefined) queryParams.append('offset', String(params.offset));
  params.categoryIds?.forEach(id => queryParams.append('categoryIds', id));
  if (params.hasFacebook) queryParams.append('hasFacebook', 'true');
  if (params.hasInstagram) queryParams.append('hasInstagram', 'true');
  if (params.hasWebsite) queryParams.append('hasWebsite', 'true');
  if (params.minRating) queryParams.append('minRating', String(params.minRating));
  if (params.sort?.length) queryParams.append('sort', params.sort.join(','));
  if (params.showRankingScore) queryParams.append('showRankingScore', 'true');
  // semanticRatio=0 is the only ratio the public endpoint honors (lexical-only opt-in).
  if (params.lexicalOnly) queryParams.append('semanticRatio', '0');

  const response = await fetch(`${API_URL}/search/stores?${queryParams}`);
  if (!response.ok) throw new Error('فشل في البحث');
  return response.json() as Promise<SearchStoresResponse>;
};

export const searchCategories = async (q: string, limit = 3): Promise<SearchCategoriesResponse> => {
  const response = await fetch(`${API_URL}/search/categories?q=${encodeURIComponent(q)}&limit=${limit}`);
  if (!response.ok) throw new Error('فشل في تحميل الاقتراحات');
  return response.json() as Promise<SearchCategoriesResponse>;
};

/**
 * Resolve a store by slug (or id) via the public GET /stores/:id endpoint,
 * which accepts either an ObjectId or a slug. Returns a minimal summary so the
 * admin can add a test case by slug without knowing the Mongo ObjectId.
 * Resolves to null when no store matches.
 */
export const resolveStoreBySlug = async (
  slugOrId: string,
): Promise<ExpectedStoreSummary | null> => {
  const response = await fetch(`${API_URL}/stores/${encodeURIComponent(slugOrId)}`);
  if (response.status === 404) return null;
  if (!response.ok) throw new Error('فشل في تحميل بيانات المتجر');
  const body = (await response.json()) as { ok?: boolean; data?: any };
  const store = body?.data;
  if (!store || !store._id) return null;
  return {
    _id: String(store._id),
    name: store.name,
    ...(store.slug ? { slug: store.slug } : {}),
    ...(store.logoUrl ? { logoUrl: store.logoUrl } : {}),
  };
};

// ─── Adapter ──────────────────────────────────────────────────────────────────

/**
 * Maps a Meilisearch StoreSearchHit to the Store type used by StoreListCard and other components.
 * Note: platform URLs are absent from search documents (only boolean flags exist).
 * StoreListCard null-checks each platform link, so an empty platforms object degrades gracefully.
 */
export const hitToStore = (hit: StoreSearchHit): Store => ({
  _id: hit.id,
  name: hit.name,
  slug: hit.slug,
  description: hit.description,
  tags: hit.tags,
  location: hit.location,
  rating: hit.rating,
  totalReviews: hit.totalReviews,
  logoUrl: hit.logoUrl,
  platforms: {},
  featured: false,
  verified: false,
  categoryId: hit.categoryIds[0] ?? '',
  category: {
    title: { ar: hit.categoryNamesAr[0] ?? '', en: '' },
    slug: hit.categorySlugs[0] ?? '',
  },
  createdAt: '',
  updatedAt: '',
});

// ─── Admin: Sync status & reindex ─────────────────────────────────────────────

export type CollectionStatus = {
  hasResumeToken: boolean;
  lastSyncedAt: string | null;
  errorCount: number;
  lastError: string | null;
  lastErrorAt: string | null;
};

export type SyncStatusData = {
  workerRunning: boolean;
  collections: {
    stores: CollectionStatus;
    categories: CollectionStatus;
    reviews: CollectionStatus;
  };
};

export type ReindexIndexResult = {
  index: string;
  ok: boolean;
  indexed: number;
  skipped: number;
  elapsedMs: number;
  error?: string;
};

export type ReindexResult = {
  ok: boolean;
  startedAt: string;
  elapsedMs: number;
  indexes: ReindexIndexResult[];
};

export const getSearchSyncStatus = async (): Promise<{ ok: boolean; data?: SyncStatusData; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/sync-status`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const triggerReindex = async (): Promise<{ ok: boolean; data?: ReindexResult; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/reindex`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return response.json();
};

// ─── Admin: Search Console ─────────────────────────────────────────────────

export type OverviewData = {
  status: string;
  version: string;
  commitSha: string;
  databaseSizeBytes: number;
  lastUpdate: string;
  indexes: Record<string, {
    numberOfDocuments: number;
    isIndexing: boolean;
    fieldDistribution: Record<string, number>;
  }>;
};

export type IndexDiagnostic = {
  index: string;
  meiliCount: number;
  expectedCount: number;
  isIndexing: boolean;
  drift: boolean;
};

export type PlaygroundParams = {
  indexName: 'stores' | 'categories' | 'reviews';
  query: string;
  semanticRatio?: number;
  includeAllStatuses?: boolean;
  categoryIds?: string[];
  governorates?: string[];
  paymentOptions?: string[];
  minRating?: number;
  hasInstagram?: boolean;
  hasFacebook?: boolean;
  hasWebsite?: boolean;
  limit?: number;
  offset?: number;
  facets?: string[];
  filter?: string;
};

export type PlaygroundResult = {
  hits: any[];
  processingTimeMs: number;
  estimatedTotalHits: number;
  facetDistribution?: Record<string, Record<string, number>>;
  query: string;
  pipeline: {
    raw: string;
    normalized: string;
    hasArabic: boolean;
    locales: string[];
    filter: string;
  };
};

export type EmbedderTestResult = {
  dimensions: number;
  latencyMs: number;
  expectedDimensions: number;
};

export const getSearchOverview = async (): Promise<{ ok: boolean; data?: OverviewData; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/overview`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getIndexDiagnostics = async (): Promise<{ ok: boolean; data?: IndexDiagnostic[]; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/diagnostics`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getIndexSettings = async (index: string): Promise<{ ok: boolean; data?: Record<string, any>; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/settings/${index}`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getSearchTasks = async (params?: { statuses?: string[]; limit?: number; index?: string }): Promise<{ ok: boolean; data?: any; error?: string }> => {
  const queryParams = new URLSearchParams();
  if (params?.statuses?.length) queryParams.append('statuses', params.statuses.join(','));
  if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params?.index) queryParams.append('index', params.index);

  const qs = queryParams.toString();
  const response = await fetch(`${getApiBaseUrl()}/admin/search/tasks${qs ? `?${qs}` : ''}`, {
    headers: getHeaders(),
  });
  return response.json();
};

// Async since quick-260722-w8u: the endpoint returns 202 { jobId } immediately
// (or 409 while a run is in flight) — poll getReindexStatus for live progress.
export const reindexSingleIndex = async (index: string): Promise<{ ok: boolean; data?: { jobId: string }; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/reindex/${index}`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return response.json();
};

export type ReindexJob = {
  jobId: string;
  index: string;
  status: 'running' | 'completed' | 'failed';
  phase: 'building' | 'settings' | 'indexing' | 'cleanup';
  totalDocuments: number;
  indexedDocuments: number;
  totalBatches: number;
  batchesDone: number;
  startedAt: string;
  finishedAt?: string;
  error?: string;
  elapsedMs: number;
};

// data is the last-known job for the index, or null if never run this dyno lifetime.
export const getReindexStatus = async (index: string): Promise<{ ok: boolean; data?: ReindexJob | null; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/reindex/${index}/status`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getIndexedDocument = async (index: string, id: string): Promise<{ ok: boolean; data?: Record<string, any>; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/documents/${index}/${encodeURIComponent(id)}`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const runPlayground = async (params: PlaygroundParams): Promise<{ ok: boolean; data?: PlaygroundResult; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/playground`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params),
  });
  return response.json();
};

export const testEmbedder = async (text: string): Promise<{ ok: boolean; data?: EmbedderTestResult; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/embedder-test`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify({ text }),
  });
  return response.json();
};

// ─── Admin: Search Quality Tooling (Plan 25-04) ─────────────────────────────
// All fns use getApiBaseUrl()+getHeaders() (admin JWT, SSR-safe) — never the
// module-top SSR API_URL const (that const is for public searchHybrid only).

// Minimal store summary the backend now attaches to each test case so the UI
// can render the expected store (link + logo + name) instead of a raw ObjectId.
export type ExpectedStoreSummary = {
  _id: string;
  name: string;
  slug?: string;
  logoUrl?: string;
};

export type SearchTestCase = {
  _id: string;
  expectedStoreId: string;
  // Populated by the backend list endpoint; null when the store was deleted.
  // Optional for backward compatibility with older payloads.
  expectedStore?: ExpectedStoreSummary | null;
  queries: string[];
  notes?: string;
  createdBy: string;
  disabled: boolean;
  createdAt: string;
  updatedAt: string;
};

export type CreateTestCaseInput = {
  expectedStoreId: string;
  queries: string[];
  notes?: string;
  disabled?: boolean;
};

export type UpdateTestCaseInput = {
  queries?: string[];
  notes?: string;
  disabled?: boolean;
};

export type TestRunResultRow = {
  testCaseId: string;
  expectedStoreId: string;
  query: string;
  rank: number | null;
  pass: boolean;
  expectedScore?: number;
  topScore?: number;
};

export type TestSuiteRun = {
  _id?: string;
  runType: 'suite';
  semanticRatio?: number;
  results: TestRunResultRow[];
  hitAt1: number;
  mrr: number;
  totalQueries: number;
  sweepId?: string;
  embeddingCalls?: number;
  createdAt?: string;
};

export type SweepRatioResult = {
  semanticRatio: number;
  hitAt1: number;
  mrr: number;
  embeddingCalls: number;
};

export type SweepResult = {
  sweepId: string;
  ratios: SweepRatioResult[];
  totalEmbeddingCalls: number;
};

export type RunDiffRow = {
  testCaseId: string;
  query: string;
  priorRank: number | null;
  latestRank: number | null;
  rankDelta: number | null;
};

export type RunDiff = {
  newlyPassed: RunDiffRow[];
  regressed: RunDiffRow[];
};

export type QueryLogReportRow = {
  normalizedQuery: string;
  count: number;
  avgResultCount?: number;
};

export type QueryLogReports = {
  zeroResults: QueryLogReportRow[];
  highVolume: QueryLogReportRow[];
};

export const listTestCases = async (): Promise<{ ok: boolean; data?: SearchTestCase[]; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-cases`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const createTestCase = async (body: CreateTestCaseInput): Promise<{ ok: boolean; data?: SearchTestCase; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-cases`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return response.json();
};

export const updateTestCase = async (id: string, body: UpdateTestCaseInput): Promise<{ ok: boolean; data?: SearchTestCase; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-cases/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: getHeaders(),
    body: JSON.stringify(body),
  });
  return response.json();
};

export const deleteTestCase = async (id: string): Promise<{ ok: boolean; data?: unknown; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-cases/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    headers: getHeaders(),
  });
  return response.json();
};

export const runTestSuite = async (params?: { semanticRatio?: number }): Promise<{ ok: boolean; data?: TestSuiteRun; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-suite/run`, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(params ?? {}),
  });
  return response.json();
};

export const runSweep = async (): Promise<{ ok: boolean; data?: SweepResult; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-suite/sweep`, {
    method: 'POST',
    headers: getHeaders(),
  });
  return response.json();
};

export const getTestRuns = async (limit?: number): Promise<{ ok: boolean; data?: TestSuiteRun[]; error?: string }> => {
  const qs = limit !== undefined ? `?limit=${limit}` : '';
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-runs${qs}`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getRunsDiff = async (): Promise<{ ok: boolean; data?: RunDiff; error?: string }> => {
  const response = await fetch(`${getApiBaseUrl()}/admin/search/test-runs/diff`, {
    headers: getHeaders(),
  });
  return response.json();
};

export const getQueryLogReports = async (params?: { limit?: number; sinceDays?: number }): Promise<{ ok: boolean; data?: QueryLogReports; error?: string }> => {
  const queryParams = new URLSearchParams();
  if (params?.limit !== undefined) queryParams.append('limit', String(params.limit));
  if (params?.sinceDays !== undefined) queryParams.append('sinceDays', String(params.sinceDays));
  const qs = queryParams.toString();
  const response = await fetch(`${getApiBaseUrl()}/admin/search/query-log/reports${qs ? `?${qs}` : ''}`, {
    headers: getHeaders(),
  });
  return response.json();
};
